import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Avatar,
  Stack,
  FormControlLabel,
  Checkbox,
  Alert,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  IconButton,
  Snackbar,
  Tooltip,
  LinearProgress,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import axios from "axios";
//* useLocation lets the page read the address, so it can find ?token=...&next=...
import { useNavigate , useLocation } from "react-router-dom";

import { getDeviceStationId, isRealPhone } from "../utils/global_functions";
import { directorUid } from "../components/constants";

import { DEFAULT_API_URL } from "../components/constants";

//* App(online) AutoLogin-> Approve Page , token: 
//  (/) is  login page.
// token is the proof of who the person is.
// next is the page to open after login
// https://attendance.aci.com.lb/?token=eyJlbXAi...&next=/home/calendar/approve


export default function LoginForm() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [openSettings, setOpenSettings] = useState(false);
  const [fullName, setFullName] = useState("");
  const [apiBaseUrl, setApiBaseUrl] = useState("");
  const [settingsError, setSettingsError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // For Token 
  const navigate = useNavigate();
  const location = useLocation();
  const linkParams = new URLSearchParams(location.search);
  const linkToken = linkParams.get("token") || "";
  const linkNext = (linkParams.get("next") || "").startsWith("/home/")
    ? linkParams.get("next")
    : "/home/calendar";

  const linkEmp = (() => {
    try {
      const part = linkToken.split(".")[0].replace(/-/g, "+").replace(/_/g, "/");
      return String(JSON.parse(atob(part)).emp ?? "");
    } catch {
      return "";
    }
  })();



  const normalizeBaseUrl = (val) => {
    if (!val) return "";
    const trimmed = val.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed.replace(/\/$/, "");
    return `https://${trimmed.replace(/\/$/, "")}`;
  };

  const SAVED_API = useMemo(
    () => localStorage.getItem("saved_sett_api_url") || DEFAULT_API_URL,
    []
  );

  useEffect(() => {
    const downloadOnce = () => {
      const a = document.createElement("a");
      a.href = "/beeattcertificate.crt";
      a.download = "beeattcertificate.crt";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };
    if (!localStorage.getItem("folderDownloaded")) {
      downloadOnce();
      localStorage.setItem("folderDownloaded", "true");
    }
  }, []);

  useEffect(() => {
    const savedUsername = localStorage.getItem("saved_username");
    const savedPassword = localStorage.getItem("saved_password");
    const savedApiUrl = localStorage.getItem("saved_sett_api_url");
    const savedFullName = localStorage.getItem("saved_sett_fullname");

    if (savedApiUrl) setApiBaseUrl(savedApiUrl);
    if (savedFullName) setFullName(savedFullName);

    if (savedUsername && savedPassword) {
      setUsername(savedUsername);
      setPassword(savedPassword);
      // if the page open through the link do not go /home auto open !
      if (localStorage.getItem("remember_me") && !linkToken) {
        navigate("/home");
      }
    }
  }, [navigate]);

    useEffect(() => {
    if (!linkToken) return;

    const sameUserLoggedIn = Boolean(
      linkEmp &&
      localStorage.getItem("saved_username") &&
      localStorage.getItem("shared_empid") === linkEmp,
    );

    const base = normalizeBaseUrl(
      localStorage.getItem("saved_sett_api_url") || DEFAULT_API_URL,
    );
    if (!base) {
      if (sameUserLoggedIn) navigate(linkNext, { replace: true });
      return;
    }

    setIsSubmitting(true);
    axios
      .post(`${base}/api/auth/magic-login`, { token: linkToken })
      .then((res) => {
        const { status, user } = res.data || {};
        if (status !== "success" || !user) throw new Error("invalid");

        const uid = String(user.UserName || "");
        if (localStorage.getItem("saved_username") !== uid) {
          localStorage.removeItem("saved_password");
          localStorage.removeItem("remember_me");
        }
        if (!localStorage.getItem("saved_sett_api_url")) {
          localStorage.setItem("saved_sett_api_url", base);
        }

        const apiFullName = user.fullname || "";
        localStorage.setItem("saved_sett_fullname", apiFullName);
        localStorage.setItem("shared_fullname", apiFullName);
        localStorage.setItem("saved_username", uid);
        localStorage.setItem("sessionToken", "bee-session");
        localStorage.setItem("shared_username", uid);
        localStorage.setItem("userrole", String(user.RoleId ?? ""));
        localStorage.setItem("saved_sett_mobilenumber", uid);
        localStorage.setItem("saved_is_spr", String(user.is_spr ?? "0"));
        localStorage.setItem("shared_empid", String(user.empId ?? user.UserCode ?? ""));

        navigate(linkNext, { replace: true });
      })
      .catch(() => {
        if (sameUserLoggedIn) {
          navigate(linkNext, { replace: true });
          return;
        }
        setErrorMessage("This link has expired. Please log in.");
        navigate(`/?next=${encodeURIComponent(linkNext)}`, { replace: true });
      })
      .finally(() => setIsSubmitting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const savedApiUrl =
        localStorage.getItem("saved_sett_api_url") || apiBaseUrl || SAVED_API;

      if (!savedApiUrl) {
        setErrorMessage("API URL not set. Please configure settings.");
        setOpenSettings(true);
        return;
      }

      const base = normalizeBaseUrl(savedApiUrl);
      const existingStationId = localStorage.getItem("device_station_id");
      const wasCleared = !existingStationId;
      const stationId = getDeviceStationId();

      const response = await axios.post(`${base}/api/login`, {
        username,
        password,
        stationId,
        wasCleared,
        isPhone: isRealPhone(),
        directorUid,
      });

      const { status, user, message } = response.data || {};



      if (status !== "success" || !user) {
        setErrorMessage(message || "Invalid username or password.");
        return;
      }

      // if API returned fullname, use it; otherwise keep current fullName state
      const apiFullName = user.fullname || user.FullName || user.full_name || "";
      
      if (apiFullName) {
        setFullName(apiFullName);
        localStorage.setItem("saved_sett_fullname", apiFullName);
        localStorage.setItem("shared_fullname", apiFullName);
      } else {
        // ensure shared_fullname still set from existing local value or state
        const fallbackFull = localStorage.getItem("saved_sett_fullname") || fullName || "";
        localStorage.setItem("shared_fullname", fallbackFull);
      }

      if (rememberMe) {
        localStorage.setItem("saved_username", username);
        localStorage.setItem("saved_password", password);
        localStorage.setItem("remember_me", "1");
      } else {
        localStorage.removeItem("remember_me");
        localStorage.removeItem("saved_username");
        localStorage.removeItem("saved_password");
      }

      localStorage.setItem("saved_username", username);
      localStorage.setItem("saved_password", password);

      localStorage.setItem("sessionToken", "bee-session");
      localStorage.setItem("shared_username", user.UserName || username);
      localStorage.setItem("userrole", String(user.RoleId ?? ""));
      localStorage.setItem("saved_sett_mobilenumber", user.UserName || "");
      localStorage.setItem("saved_is_spr", user.is_spr || "0");

      const empId =
       user.EmpId ?? user.UserCode;

        localStorage.setItem("shared_empid", String(empId || ""));

      //navigate("/home/calendar");
      navigate(linkNext);
    } catch (error) {
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage("Login failed. Check your network or server.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveSettings = () => {
    if (!fullName || !apiBaseUrl) {
      setSettingsError("Please fill in all required fields.");
      return;
    }
    setSettingsError("");
    localStorage.setItem("saved_sett_fullname", fullName);
    localStorage.setItem("saved_sett_api_url", apiBaseUrl);
    setOpenSettings(false);
    setSnack({ open: true, message: "Settings saved.", severity: "success" });
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        p: { xs: 2, md: 3 },
        // Light base with teal accents
        background: `
          radial-gradient(900px 500px at -10% -20%, rgba(200, 230, 201, 0.7), transparent 65%),
          radial-gradient(900px 500px at 120% 120%, rgba(178, 223, 219, 0.85), transparent 65%),
          linear-gradient(135deg, #ffffff 0%, #e0f2f1 40%, #ffffff 100%)
        `,
        "&:before": {
          content: "''",
          position: "absolute",
          inset: 0,
          background: `url('/bg-texture.svg') center/cover no-repeat`,
          opacity: 0.04,
          pointerEvents: "none",
        },
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: 440,
          maxWidth: "100%",
          borderRadius: 4,
          overflow: "hidden",
          backdropFilter: "blur(10px)",
          bgcolor: "rgba(255,255,255,0.98)",
          boxShadow:
            "0 10px 30px rgba(0,0,0,.08), 0 20px 60px rgba(0, 150, 136, .25)",
          border: "1px solid",
          borderColor: "#80cbc4",
        }}
      >
        {isSubmitting && (
          <LinearProgress
            color="info"
            sx={{ borderRadius: "4px 4px 0 0" }}
          />
        )}

        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          <Stack alignItems="center" spacing={2}>
            <Avatar
              sx={{
                bgcolor: "#009688",
                width: { xs: 56, md: 68 },
                height: { xs: 56, md: 68 },
                boxShadow: "0 8px 24px rgba(0, 150, 136, .6)",
              }}
            >
              <FingerprintIcon fontSize="large" />
            </Avatar>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, color: "#004d40", letterSpacing: 0.4 }}
              >
                Bee Attendance
              </Typography>
              <Typography variant="body2" sx={{ color: "#607d8b" }}>
                Fast • Smart • Connected
              </Typography>
            </Box>
          </Stack>

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: { xs: 2, md: 3 } }}>
            <TextField
              fullWidth
              label="Username"
              margin="normal"
              variant="outlined"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: "#009688" }} />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ sx: { color: "#78909c" } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "& fieldset": { borderColor: "rgba(128, 203, 196, 0.9)" },
                  "&:hover fieldset": {
                    borderColor: "#4db6ac",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#009688",
                  },
                },
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              margin="normal"
              variant="outlined"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: "#009688" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ color: "#009688" }} />
                      ) : (
                        <Visibility sx={{ color: "#009688" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ sx: { color: "#78909c" } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "& fieldset": { borderColor: "rgba(128, 203, 196, 0.9)" },
                  "&:hover fieldset": {
                    borderColor: "#4db6ac",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#009688",
                  },
                },
              }}
            />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                sx={{ color: "#546e7a" }}
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: "#80cbc4",
                      "&.Mui-checked": { color: "#009688" },
                    }}
                  />
                }
                label="Remember me"
              />
            </Stack>

            <LoadingButton
              fullWidth
              loading={isSubmitting}
              type="submit"
              variant="contained"
              sx={{
                mt: 2,
                py: 1.2,
                fontWeight: 700,
                letterSpacing: 0.3,
                bgcolor: "#009688",
                ":hover": { bgcolor: "#00796b" },
                boxShadow: "0 10px 24px rgba(0, 150, 136, .45)",
                borderRadius: 2,
              }}
            >
              Log In
            </LoadingButton>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 1.5 }}
            >
              {SAVED_API && (
                <Typography variant="caption" sx={{ color: "#78909c", wordBreak: "break-all" }}>
                  API: {SAVED_API}
                </Typography>
              )}
            </Stack>
          </Box>

          <Divider sx={{ my: 2, borderColor: "rgba(128, 203, 196, 0.8)" }} />

          <Stack direction="row" justifyContent="center" spacing={1}>
            <Typography variant="caption" sx={{ color: "#90a4ae" }}>
              © {new Date().getFullYear()} Bee Attendance
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={openSettings}
        onClose={() => setOpenSettings(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            background:
              "linear-gradient(135deg, #e0f2f1 0%, #ffffff 40%, #e0f7fa 100%)",
            boxShadow: "0 24px 64px rgba(0,0,0,.22)",
            border: "1px solid #80cbc4",
          },
        }}
      >
        {settingsError && (
          <Alert severity="error" sx={{ m: 2, mb: 0 }}>
            {settingsError}
          </Alert>
        )}

        <DialogTitle sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar sx={{ bgcolor: "#009688", width: 44, height: 44 }}>
              <FingerprintIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: "#004d40" }}
              >
                General Info
              </Typography>
              <Typography variant="body2" sx={{ color: "#607d8b" }}>
                Application basic configuration
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid #80cbc4",
                bgcolor: "#e0f2f1",
                p: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "#004d40", mb: 1.5 }}
              >
                General Info
              </Typography>

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={fullName}
                  required
                  onChange={(e) => {
                    setFullName(e.target.value);
                    localStorage.setItem("saved_sett_fullname", e.target.value);
                  }}
                  variant="outlined"
                />

                <TextField
                  fullWidth
                  label="API Base URL (IP:PORT or full URL)"
                  value={apiBaseUrl}
                  required
                  onChange={(e) => setApiBaseUrl(e.target.value)}
                  helperText="Examples: 10.0.0.12:5100  •  https://api.example.com"
                  variant="outlined"
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", p: 3, pt: 1 }}>
          <Button
            onClick={() => setOpenSettings(false)}
            variant="text"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveSettings}
            variant="contained"
            sx={{
              bgcolor: "#009688",
              ":hover": { bgcolor: "#00796b" },
              boxShadow: "0 10px 24px rgba(0, 150, 136, .45)",
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Tooltip title="Open settings">
        <Fab
          color="primary"
          size="medium"
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            boxShadow: "0 10px 24px rgba(0, 150, 136, .6)",
            bgcolor: "#009688",
            ":hover": { bgcolor: "#00796b" },
          }}
          onClick={() => setOpenSettings(true)}
          aria-label="open settings"
        >
          <SettingsIcon />
        </Fab>
      </Tooltip>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
}
